import {
  DragEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { SESSION_DRAG_TYPE } from './constants';
import { useLongPress } from './hooks';
import { MenuItem, MenuPositioned, initialClamp } from './menu';
import { toLocalPosition } from './menu-position';
import { SessionList } from './SessionRow';

interface FolderInlineRenameProps {
  readonly initialValue: string;
  readonly onCommit: (name: string) => void;
  readonly onCancel: () => void;
}

export function FolderInlineRename({
  initialValue,
  onCommit,
  onCancel,
}: FolderInlineRenameProps) {
  const [draft, setDraft] = useState(initialValue);
  return (
    <FolderInlineInput
      // Inline rename mounts only on user intent (context-menu
      // "Rename"); auto-focusing avoids a redundant click.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onCommit(draft);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

interface FolderRowWithLongPressProps {
  readonly children: ReactNode;
  readonly $open: boolean;
  readonly onClick: () => void;
  readonly onLongPress: (coords: { x: number; y: number }) => void;
}

// Tiny wrapper so the closure-rendered folder row gets its own
// long-press refs (one per folder). The `consumeFired()` guard
// prevents the touch-end click from immediately collapsing the
// folder after the long-press opens its menu.
export function FolderRowWithLongPress({
  children,
  $open,
  onClick,
  onLongPress,
}: FolderRowWithLongPressProps) {
  const { touchHandlers, consumeFired } = useLongPress(onLongPress);
  return (
    <FolderRow
      $open={$open}
      onClick={() => {
        if (consumeFired()) return;
        onClick();
      }}
      {...touchHandlers}
    >
      {children}
    </FolderRow>
  );
}

export function FolderContextMenu({
  x,
  y,
  pinned,
  onClose,
  onPin,
  onRename,
  onDelete,
}: {
  readonly x: number;
  readonly y: number;
  readonly pinned: boolean;
  readonly onClose: () => void;
  readonly onPin: () => void;
  readonly onRename: () => void;
  readonly onDelete: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState<{ x: number; y: number }>(() =>
    initialClamp(x, y),
  );

  useEffect(() => {
    function onClickAway(e: globalThis.MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest('[data-context-menu]') != null) return;
      // The folder ellipsis toggles this menu; ignore its mousedown so the
      // click-away doesn't close-then-reopen (leaving it stuck open).
      if (target.closest('[data-menu-toggle]') != null) return;
      onClose();
    }
    function onEsc(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Claim the key — see AccountMenu: an open menu is the top layer, so one
      // press must not also reach a modal listening underneath it.
      e.stopPropagation();
      onClose();
    }
    window.addEventListener('mousedown', onClickAway, true);
    window.addEventListener('keydown', onEsc, true);
    return () => {
      window.removeEventListener('mousedown', onClickAway, true);
      window.removeEventListener('keydown', onEsc, true);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const maxX = window.innerWidth - rect.width - margin;
    const maxY = window.innerHeight - rect.height - margin;
    // Clamped in screen space (rect and innerWidth/innerHeight agree there),
    // then converted into the element's own space — see menu-position: under UI
    // scale a screen coordinate written straight to left/top is scaled twice.
    setClamped(
      toLocalPosition(
        el,
        Math.max(margin, Math.min(x, maxX)),
        Math.max(margin, Math.min(y, maxY)),
      ),
    );
  }, [x, y]);

  return createPortal(
    <MenuPositioned
      ref={ref}
      data-context-menu
      style={{ left: clamped.x, top: clamped.y }}
    >
      <MenuItem
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onPin();
        }}
      >
        {pinned ? 'Unpin' : 'Pin'}
      </MenuItem>
      <MenuItem
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onRename();
        }}
      >
        Rename
      </MenuItem>
      <MenuItem
        $danger
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        Delete
      </MenuItem>
    </MenuPositioned>,
    document.body,
  );
}

export const FolderSection = styled.div`
  display: flex;
  flex-direction: column;
`;

interface SessionDropZoneProps {
  /** Target folder id (or `DEFAULT_FOLDER_ID` for the unfiled sessions list). */
  readonly folderId: string;
  /** Called with the dropped session id + folder id when a SessionRow lands. */
  readonly onDropSession: (sessionId: string, folderId: string) => void;
  readonly children: ReactNode;
}

// Drop target for drag-and-drop chat-into-folder moves. Wraps a folder
// section (header row + its children) or the unfiled sessions list so the
// whole area accepts drops, not just the bare label — easier to hit on the
// first try. Custom MIME type means foreign drags (text selections, files)
// don't show a drop affordance.
//
// `dragenter` / `dragleave` fire on every nested child, so we keep a depth
// counter — `over` only flips off when leaving the outermost wrapper.
export function SessionDropZone({
  folderId,
  onDropSession,
  children,
}: SessionDropZoneProps) {
  const [over, setOver] = useState(false);
  const depthRef = useRef(0);

  const isSessionDrag = useCallback((e: DragEvent<HTMLDivElement>) => {
    // `types` is a DOMStringList-shaped array; Array.from for `.includes`.
    return Array.from(e.dataTransfer.types).includes(SESSION_DRAG_TYPE);
  }, []);

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isSessionDrag(e)) return;
      depthRef.current += 1;
      setOver(true);
    },
    [isSessionDrag],
  );

  // `dragover` must `preventDefault` so the browser allows a drop here —
  // otherwise the drop event never fires and the row snaps back.
  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isSessionDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    [isSessionDrag],
  );

  const handleDragLeave = useCallback(() => {
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) setOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isSessionDrag(e)) return;
      e.preventDefault();
      const sessionId = e.dataTransfer.getData(SESSION_DRAG_TYPE);
      depthRef.current = 0;
      setOver(false);
      if (sessionId.length === 0) return;
      onDropSession(sessionId, folderId);
    },
    [isSessionDrag, folderId, onDropSession],
  );

  return (
    <DropZoneBox
      $over={over}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
    </DropZoneBox>
  );
}

const DropZoneBox = styled.div<{ $over: boolean }>`
  border-radius: 6px;
  outline: 1px solid transparent;
  outline-offset: -1px;
  transition:
    outline-color 0.1s ease,
    background 0.1s ease;
  ${(p) =>
    p.$over &&
    css`
      outline-color: var(--border-color);
      background: var(--hover-bg);
    `}
`;

export const FolderRow = styled.div<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 12px;
  border-radius: 6px;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: var(--hover-bg);
    color: var(--text-primary);
  }
`;

// Both the closed and open folder glyphs stay mounted, stacked via
// `FolderIconLayer`, and cross-fade via opacity — swapping which one is
// visible instead of unmounting/remounting avoids the blank gap an
// enter-only animation leaves between the outgoing icon disappearing
// and the incoming one fading in.
export const FolderIconSwap = styled.span`
  position: relative;
  display: inline-flex;
  width: 14px;
  height: 12px;
  flex-shrink: 0;
`;

export const FolderIconLayer = styled.span<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: inline-flex;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 150ms ease;
`;

export const FolderLabel = styled.span`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const FolderInlineInput = styled.input`
  flex: 1;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid var(--accent-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: inherit;
  outline: none;
`;

// Shared chrome for the hover-revealed action buttons at the right edge
// of a folder row (new-chat compose + ellipsis menu). Hidden until the
// row is hovered or the button itself is keyboard-focused.
const folderHoverButtonCss = css`
  border: none;
  background: transparent;
  color: var(--text-secondary);
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  flex-shrink: 0;
  transition: opacity 0.1s ease;

  ${FolderRow}:hover & {
    opacity: 1;
  }

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }

  &:focus-visible {
    opacity: 1;
  }
`;

export const FolderEllipsisButton = styled.button`
  ${folderHoverButtonCss}
  font-size: 14px;
`;

export const FolderNewChatButton = styled.button`
  ${folderHoverButtonCss}
  display: inline-flex;
  align-items: center;
`;

export const FolderEmpty = styled.div`
  padding: 4px 12px 8px 28px;
  color: var(--text-label);
  font-size: 11px;
  opacity: 0.7;
`;

// Sessions inside a folder render indented with a faint left rail so
// the user can see at a glance which rows belong to the folder above
// and which are unfiled at the root.
export const FolderSessionList = styled(SessionList)`
  margin-left: 18px;
  padding-left: 8px;
  border-left: 1px solid var(--divider-color);
`;

// Folder-collapse animation duration. Matches ChatGPT's sidebar folder
// expand/collapse timing.
const FOLDER_COLLAPSE_DURATION_MS = 200;

// Animates a folder's children open/closed via the `grid-template-rows`
// 0fr/1fr trick — lets a `height: auto`-sized block transition smoothly
// without measuring its height in JS. `FolderContentInner`'s `overflow:
// hidden` clips the row during the animation; `min-height: 0` on this
// element lets the grid track actually shrink to 0fr instead of clamping
// to the content's intrinsic height.
export const FolderContent = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows ${FOLDER_COLLAPSE_DURATION_MS}ms ease;
  min-height: 0;
`;

// Stays stretched to the animating row track (grid's default
// `align-items: stretch`) so its own `overflow: hidden` clips the list
// as the track shrinks. Rows inside must keep `flex-shrink: 0` (see
// `Row` in SessionRow.tsx) or they'd compress to fit instead of being
// clipped, which reads as the folder "skewing" shut.
export const FolderContentInner = styled.div`
  overflow: hidden;
`;
